import { SlksDate } from "../dto/date";
import { computed } from "vue";

// default: yyyy-mm-dd
// slash: dd/mm yyyy
// year: yyyy
export type SlksDateFormat = "default" | "slash" | "year";

export function useFormatDate() {
    const formatDate = (value: string | null | undefined, format: SlksDateFormat): string => {
        if (!value) return "";

        const date = new Date(value);
        const year = date.getFullYear().toString(10).padStart(4, "0");
        const month = (date.getMonth() + 1).toString(10).padStart(2, "0");
        const day = date.getDate().toString(10).padStart(2, "0");

        switch (format) {
            case "year":
                return year;
            case "slash":
                return `${day}/${month} ${year}`;
            default:
                return [year, month, day].join("-");
        }
    };

    return { formatDate };
}

export function useFormattedDate(value: SlksDate | null | undefined, format: SlksDateFormat, period: boolean) {
    const { formatDate } = useFormatDate();

    const formattedDate = computed(() => {
        if (!value) return "";

        if (period && value.startDate && value.endDate) {
            return `${formatDate(value.startDate, format)} - ${formatDate(value.endDate, format)}`;
        }
        return (
            formatDate(value.precisionDate, format) ||
            formatDate(value.endDate, format) ||
            formatDate(value.startDate, format) ||
            ""
        );
    });

    return { formattedDate };
}

export function useFormattedEsDate(
    startDate: string | null | undefined,
    endDate: string | null | undefined,
    format: "year" | "auto",
    period: boolean,
    autoPeriod: null | boolean | undefined
) {
    const formattedEsDate = computed(() => {
        const formatDate = (date: string | null | undefined, format: "year" | "auto"): string => {
            if (!date) {
                return "";
            }

            if (format === "year") {
                const yearMatch = date.match(/-?\d{4}/);
                return yearMatch ? yearMatch[0] : "";
            }
            return date;
        };

        const hasBothDates = startDate && endDate;

        if (autoPeriod) {
            if (format === "year") {
                const startYear = formatDate(startDate, "year");
                const endYear = formatDate(endDate, "year");

                if (startYear === endYear || !hasBothDates) {
                    return startYear || endYear;
                }

                return `${startYear} - ${endYear}`.trim();
            } else {
                if (startDate === endDate || !hasBothDates) {
                    return formatDate(startDate || endDate, "auto");
                } else {
                    return `${formatDate(startDate, "auto")} - ${formatDate(endDate, "auto")}`.trim();
                }
            }
        }

        if (period === false) {
            return formatDate(endDate, format);
        }

        if (hasBothDates) {
            return `${formatDate(startDate, format)} - ${formatDate(endDate, format)}`;
        } else {
            return formatDate(startDate || endDate, format);
        }
    });

    return { formattedEsDate };
}
